import React from 'react';
import { motion } from 'framer-motion';
import { Sun, Flame, Shield, Leaf } from 'lucide-react';
import { nachhaltigkeitData, projectData } from '@/data/mock';

const NachhaltigkeitView = () => {
  const iconMap = {
    sun: Sun,
    flame: Flame,
    shield: Shield
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d1a0d] via-[#1a2e1a] to-[#0d1a0d] px-5 py-8 md:p-16 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-[#C9A961]/5 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Hero Header with Image */}
        <div className="relative rounded-3xl overflow-hidden mb-12 md:mb-20 h-auto min-h-[420px] md:h-[500px] group">
          <img
            src={projectData.images.exterior2}
            alt="Green Living – Leben am Wasser, Außenansicht · unverbindliche Visualisierung, mit KI bearbeitet (AI MODIFIED)"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
          />
          <span className="absolute top-4 right-4 z-20 text-[10px] uppercase tracking-widest text-white/70 bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full border border-white/10">
            Visualisierung
          </span>
          <img
            src="/badges/ai_modified_white.png"
            alt="Mit KI bearbeitet (AI MODIFIED)"
            className="absolute bottom-4 left-4 z-20 h-5 md:h-6 w-auto opacity-95 drop-shadow-md"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d1a0d]/50 via-[#0d1a0d]/45 to-[#0d1a0d]/95" />
          
          {/* Gold Corner Accents */}
          <div className="absolute top-0 left-0 w-32 h-32">
            <div className="absolute top-0 left-0 w-20 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
            <div className="absolute top-0 left-0 h-20 w-px bg-gradient-to-b from-[#D4AF37] to-transparent" />
          </div>
          
          <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Decorative Element */}
              <div className="flex items-center justify-center gap-4 mb-8">
                <div className="w-20 h-px bg-gradient-to-r from-transparent to-[#D4AF37]" />
                <Leaf className="w-6 h-6 text-[#D4AF37]" />
                <div className="w-20 h-px bg-gradient-to-l from-transparent to-[#D4AF37]" />
              </div>
              
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-light text-white mb-4 md:mb-6 tracking-tight" style={{ fontFamily: "'Cormorant', serif" }}>
                {nachhaltigkeitData.title}
              </h1>
              
              <p className="text-xl text-[#D4AF37] mb-8 font-light tracking-wider">
                {nachhaltigkeitData.subtitle}
              </p>
              
              <div className="h-px w-32 mx-auto mb-8 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
              
              <p className="text-lg md:text-xl text-white/80 max-w-4xl mx-auto font-light leading-relaxed">
                {nachhaltigkeitData.intro}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-10 md:space-y-16 mb-12 md:mb-20">
          {nachhaltigkeitData.features.map((feature, index) => {
            const Icon = iconMap[feature.icon];
            return (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, x: index % 2 === 0 ? -60 : 60 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.9, delay: index * 0.2 }}
                className="luxury-card luxury-blur rounded-3xl p-6 md:p-14 border border-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all duration-500 group relative overflow-hidden"
              >
                {/* Hover Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/0 via-[#D4AF37]/5 to-[#D4AF37]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                
                <div className="flex flex-col md:flex-row gap-6 md:gap-10 items-start relative z-10">
                  <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br from-[#D4AF37]/20 to-[#C9A961]/20 flex items-center justify-center border border-[#D4AF37]/30">
                    <Icon className="w-10 h-10 text-[#D4AF37]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-3xl md:text-5xl font-light text-white mb-2" style={{ fontFamily: "'Cormorant', serif" }}>
                      {feature.title}
                    </h3>
                    <p className="text-[#F4E4C1] text-lg mb-6 font-light tracking-wide">
                      {feature.subtitle}
                    </p>
                    <div className="h-px w-20 mb-6 bg-gradient-to-r from-[#D4AF37] to-transparent" />
                    <p className="text-white/80 text-lg leading-relaxed font-light">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Results Grid */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-5xl font-light text-white mb-6" style={{ fontFamily: "'Cormorant', serif" }}>
              Das Ergebnis
            </h3>
            <div className="h-px w-24 mx-auto bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {nachhaltigkeitData.results.map((result, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                className="luxury-card luxury-blur rounded-2xl p-5 md:p-8 text-center border border-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all duration-500 group"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-[#C9A961]/20 flex items-center justify-center mx-auto mb-4">
                  <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                </div>
                <p className="text-[#F4E4C1] text-xs uppercase tracking-widest mb-3 font-light">
                  {result.label}
                </p>
                <p className="text-white text-xl md:text-3xl font-light" lang="de" style={{ fontFamily: "'Cormorant', serif", hyphens: 'auto', WebkitHyphens: 'auto', overflowWrap: 'break-word' }}>
                  {result.value}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
      
      {/* Grain Texture */}
      <div className="grain-texture absolute inset-0 pointer-events-none" />
    </div>
  );
};

export default NachhaltigkeitView;
